import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createOpenWeekInputSchema, PLANNING_LIMITS } from '@wwaave/shared/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  AvailabilityCalendar,
  type CalendarEntry,
  type CalendarExchange,
} from '../../components/calendar/AvailabilityCalendar';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { ApiError, useApiForm } from '../../hooks/useApiForm';
import { fetchOk } from '../../lib/api';
import { csrfHeaders } from '../../lib/auth-context';
import { formatDateRangeMs } from '../../lib/date-format';
import { showToast } from '../../lib/toast';

export const Route = createFileRoute('/host/availability')({
  component: HostAvailabilityPage,
});

type VenueRow = {
  id: string;
  name: string;
  slug: string;
  city: string;
  status: 'draft' | 'pending_review' | 'live' | 'paused' | 'removed';
};

type OpenWeekDTO = {
  id: string;
  venueId: string;
  fromDate: number;
  toDate: number;
  notes: string | null;
  createdAt: number;
  updatedAt: number;
};

type CalendarData = {
  entries: CalendarEntry[];
  exchanges: CalendarExchange[];
  defaultState: 'available' | 'unavailable';
};

type FormValues = {
  from_date: string;
  to_date: string;
  notes: string;
};

const INITIAL_FORM: FormValues = {
  from_date: '',
  to_date: '',
  notes: '',
};

const DAY_MS = 24 * 60 * 60 * 1000;

function HostAvailabilityPage(): React.JSX.Element {
  const [venues, setVenues] = useState<VenueRow[] | null>(null);
  const [venuesError, setVenuesError] = useState<string | null>(null);
  const [activeVenueId, setActiveVenueId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOk('/api/venues/me/venues', { credentials: 'include' });
        const json = (await res.json()) as { ok: boolean; venues?: VenueRow[] };
        if (cancelled) return;
        const list = json.venues ?? [];
        setVenues(list);
        if (list.length > 0 && list[0] !== undefined) {
          setActiveVenueId(list[0].id);
        }
      } catch (err) {
        if (cancelled) return;
        setVenuesError(
          err instanceof ApiError ? err.message : "We couldn't load your venues.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell tone="content">
      <Stack gap="xl">
        <PageHero
          eyebrow="Hosting"
          title="Availability"
          lede="Manage your calendar and show artists when your space is open."
        />

        {venuesError !== null ? (
          <Text c="red" role="alert">
            {venuesError}
          </Text>
        ) : venues === null ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : venues.length === 0 ? (
          <MotionSection>
            <Box style={{ maxWidth: 640, marginInline: 'auto' }}>
              <EmptyState
                title="Add a venue first."
                body="Your availability calendar attaches to a venue. Once you have one, you can manage your dates here."
                action={
                  <Button
                    component={Link}
                    to="/host/venues"
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  >
                    Go to venues
                  </Button>
                }
              />
            </Box>
          </MotionSection>
        ) : venues.length === 1 && venues[0] !== undefined ? (
          <MotionSection>
            <VenueEditor venue={venues[0]} />
          </MotionSection>
        ) : (
          <MotionSection>
            <Tabs
              value={activeVenueId}
              onChange={(value) => {
                if (value !== null) setActiveVenueId(value);
              }}
              variant="outline"
            >
              <Tabs.List>
                {venues.map((v) => (
                  <Tabs.Tab key={v.id} value={v.id}>
                    {v.name}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {venues.map((v) => (
                <Tabs.Panel key={v.id} value={v.id} pt="lg">
                  <VenueEditor venue={v} />
                </Tabs.Panel>
              ))}
            </Tabs>
          </MotionSection>
        )}
      </Stack>
    </AppShell>
  );
}

function VenueContextStrip({ venue }: { venue: VenueRow }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
      <Stack gap={2}>
        <Group gap="xs" align="center">
          <Text fw={600} size="lg">
            {venue.name}
          </Text>
          {venue.status !== 'live' ? (
            <Badge size="sm" variant="light" color="gray" tt="capitalize">
              {venue.status.replace('_', ' ')}
            </Badge>
          ) : null}
        </Group>
        <Text size="sm" c="dimmed">
          {venue.city}
        </Text>
      </Stack>
      {venue.status === 'live' ? (
        <Link
          to="/venues/$slug"
          params={{ slug: venue.slug }}
          style={{ textDecoration: 'none' }}
        >
          <Anchor component="span" size="sm">
            View public page
          </Anchor>
        </Link>
      ) : null}
    </Group>
  );
}

function VenueEditor({ venue }: { venue: VenueRow }): React.JSX.Element {
  const queryClient = useQueryClient();

  const calendarQuery = useQuery<CalendarData>({
    queryKey: ['venue-calendar', venue.id],
    queryFn: async () => {
      const res = await fetchOk(
        `/api/venue-calendar/${encodeURIComponent(venue.id)}/calendar`,
        { credentials: 'include' },
      );
      return (await res.json()) as CalendarData;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (params: { dates: string[]; state: 'blocked' | 'available' }) => {
      const res = await fetchOk(
        `/api/venue-calendar/${encodeURIComponent(venue.id)}/calendar/entries`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            ...csrfHeaders(),
            'content-type': 'application/json',
          },
          body: JSON.stringify(params),
        },
      );
      return (await res.json()) as { ok: boolean; entries: CalendarEntry[] };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<CalendarData>(['venue-calendar', venue.id], (prev) => {
        if (!prev) return prev;
        return { ...prev, entries: data.entries };
      });
    },
    onError: (err) => {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't update the calendar.",
      });
    },
  });

  const handleToggleDates = useCallback(
    (dates: string[], newState: 'blocked' | 'available') => {
      toggleMutation.mutate({ dates, state: newState });
    },
    [toggleMutation],
  );

  const [weeks, setWeeks] = useState<OpenWeekDTO[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadWeeks = useCallback(async (): Promise<void> => {
    setLoadError(null);
    try {
      const res = await fetchOk(
        `/api/open-weeks/host/venue/${encodeURIComponent(venue.id)}`,
        { credentials: 'include' },
      );
      const json = (await res.json()) as { items: OpenWeekDTO[] };
      setWeeks(json.items);
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? err.message : "We couldn't load your open weeks.",
      );
    }
  }, [venue.id]);

  useEffect(() => {
    void loadWeeks();
  }, [loadWeeks]);

  const {
    form,
    submit,
    submitting,
    serverError: submitError,
  } = useApiForm<FormValues>({
    endpoint: `/api/open-weeks/host/venue/${encodeURIComponent(venue.id)}`,
    schema: createOpenWeekInputSchema,
    initialValues: INITIAL_FORM,
    transform: (values) => {
      const body: Record<string, unknown> = {
        from_date: values.from_date,
        to_date: values.to_date,
      };
      if (values.notes.trim().length > 0) body['notes'] = values.notes.trim();
      return body;
    },
    onSuccess: async () => {
      showToast({ kind: 'success', message: 'Open week added.' });
      form.setValues(INITIAL_FORM);
      form.reset();
      await loadWeeks();
    },
    mapError: (err) =>
      err instanceof ApiError ? err.message : "We couldn't save that open week.",
  });

  async function handleRemove(weekId: string): Promise<void> {
    setRemovingId(weekId);
    try {
      await fetchOk(
        `/api/open-weeks/host/venue/${encodeURIComponent(venue.id)}/${encodeURIComponent(weekId)}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: csrfHeaders(),
        },
      );
      showToast({ kind: 'success', message: 'Open week removed.' });
      await loadWeeks();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't remove that.",
      });
    } finally {
      setRemovingId(null);
    }
  }

  const { upcoming, past } = useMemo(() => splitWeeks(weeks), [weeks]);

  return (
    <Stack gap="lg">
      <VenueContextStrip venue={venue} />

      {calendarQuery.isLoading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : calendarQuery.isError ? (
        <Text c="red" size="sm">
          Calendar couldn&apos;t load. The open-weeks editor below still works.
        </Text>
      ) : calendarQuery.data ? (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Click or drag to toggle dates. Teal is available, grey is blocked.
          </Text>
          <AvailabilityCalendar
            entries={calendarQuery.data.entries}
            exchanges={calendarQuery.data.exchanges}
            defaultState={calendarQuery.data.defaultState}
            onToggleDates={handleToggleDates}
          />
        </Stack>
      ) : null}

      <Divider label="Open weeks (quick-add)" labelPosition="left" />

      <Grid gap="lg" align="flex-start">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <PostedWeeksColumn
            loadError={loadError}
            weeks={weeks}
            upcoming={upcoming}
            past={past}
            removingId={removingId}
            onRemove={handleRemove}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Box
            style={{
              position: 'sticky',
              top: 'calc(64px + var(--mantine-spacing-md))',
            }}
          >
            <AddOpenWeekCard
              form={form}
              submit={submit}
              submitting={submitting}
              submitError={submitError}
            />
          </Box>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function PostedWeeksColumn({
  loadError,
  weeks,
  upcoming,
  past,
  removingId,
  onRemove,
}: {
  loadError: string | null;
  weeks: OpenWeekDTO[] | null;
  upcoming: OpenWeekDTO[];
  past: OpenWeekDTO[];
  removingId: string | null;
  onRemove: (id: string) => Promise<void>;
}) {
  if (loadError !== null) {
    return (
      <Text c="red" role="alert">
        {loadError}
      </Text>
    );
  }
  if (weeks === null) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }
  if (weeks.length === 0) {
    return (
      <Box style={{ maxWidth: 560, marginInline: 'auto' }}>
        <EmptyState
          title="No open weeks yet."
          body="Post your first window using the form on the right. Artists planning a tour through your city will see it on your venue page."
        />
      </Box>
    );
  }

  return (
    <Stack gap="xl">
      <Stack gap="sm">
        <Group justify="space-between" align="baseline">
          <Text fw={600} size="sm" tt="uppercase" c="ink.7" style={{ letterSpacing: '0.05em' }}>
            Upcoming
          </Text>
          <Text size="xs" c="dimmed">
            {upcoming.length} {upcoming.length === 1 ? 'window' : 'windows'}
          </Text>
        </Group>
        {upcoming.length === 0 ? (
          <Card withBorder padding="md" radius="md" bg="ink.0">
            <Text size="sm" c="dimmed">
              No upcoming windows. Post one using the form on the right.
            </Text>
          </Card>
        ) : (
          <Stack gap="sm">
            {upcoming.map((week) => (
              <OpenWeekCard
                key={week.id}
                week={week}
                removing={removingId === week.id}
                onRemove={() => void onRemove(week.id)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {past.length > 0 ? (
        <Stack gap="sm">
          <Divider
            label={
              <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                Past
              </Text>
            }
            labelPosition="left"
          />
          <Stack gap="sm">
            {past.map((week) => (
              <OpenWeekCard
                key={week.id}
                week={week}
                removing={removingId === week.id}
                onRemove={() => void onRemove(week.id)}
                muted
              />
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}

function OpenWeekCard({
  week,
  removing,
  onRemove,
  muted = false,
}: {
  week: OpenWeekDTO;
  removing: boolean;
  onRemove: () => void;
  muted?: boolean;
}) {
  const days = Math.max(1, Math.round((week.toDate - week.fromDate) / DAY_MS) + 1);
  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      style={muted ? { opacity: 0.65 } : undefined}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" align="center" wrap="wrap">
            <Badge variant="light" color={muted ? 'gray' : 'brand'} w="fit-content">
              {formatDateRangeMs(week.fromDate, week.toDate)}
            </Badge>
            <Text size="xs" c="dimmed">
              {days} {days === 1 ? 'day' : 'days'}
            </Text>
          </Group>
          {week.notes !== null && week.notes.length > 0 ? (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {week.notes}
            </Text>
          ) : null}
        </Stack>
        <Button
          size="xs"
          color="red"
          variant="subtle"
          loading={removing}
          onClick={onRemove}
        >
          Remove
        </Button>
      </Group>
    </Card>
  );
}

function AddOpenWeekCard({
  form,
  submit,
  submitting,
  submitError,
}: {
  form: ReturnType<typeof useApiForm<FormValues>>['form'];
  submit: ReturnType<typeof useApiForm<FormValues>>['submit'];
  submitting: boolean;
  submitError: string | null;
}) {
  return (
    <Paper withBorder p="lg" radius="md">
      <form onSubmit={submit}>
        <Stack gap="md">
          <Stack gap={2}>
            <Title order={5}>Add an open week</Title>
            <Text size="xs" c="dimmed">
              A window you'd love to host in.
            </Text>
          </Stack>
          <Group grow>
            <TextInput
              type="date"
              label="From"
              required
              {...form.getInputProps('from_date')}
            />
            <TextInput
              type="date"
              label="To"
              required
              {...form.getInputProps('to_date')}
            />
          </Group>
          <Textarea
            label="Notes"
            description="Anything artists should know? (optional)"
            placeholder="e.g. weekends are best; we can host 2-3 acts."
            autosize
            minRows={2}
            maxRows={6}
            maxLength={PLANNING_LIMITS.openWeekNotesMax}
            {...form.getInputProps('notes')}
          />
          {submitError !== null ? (
            <Text c="red" size="sm" role="alert">
              {submitError}
            </Text>
          ) : null}
          <Button
            type="submit"
            loading={submitting}
            variant="gradient"
            gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            fullWidth
          >
            Post window
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}

function splitWeeks(weeks: OpenWeekDTO[] | null): {
  upcoming: OpenWeekDTO[];
  past: OpenWeekDTO[];
} {
  if (weeks === null) return { upcoming: [], past: [] };
  const todayStartMs = startOfTodayMs();
  const upcoming: OpenWeekDTO[] = [];
  const past: OpenWeekDTO[] = [];
  for (const w of weeks) {
    if (w.toDate >= todayStartMs) upcoming.push(w);
    else past.push(w);
  }
  past.reverse();
  return { upcoming, past };
}

function startOfTodayMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
